import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../../../../context";
import { useLocation, useNavigate } from "react-router-dom";
import useFetchObjects from "../../../../../api/useFetchObjects";
import useUpdate from "../../../../../api/useUpdate";
import { useTranslation } from "react-i18next";

// Import the separate dialog component
import ChangePasswordDialog from "./ChangePasswordDialog";

const EmployeeEdit = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const location = useLocation();
  const userData = location.state;
  const navigate = useNavigate();

  // State to manage the "Change Password" dialog
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const handleOpenDialog = () => setShowPasswordDialog(true);
  const handleCloseDialog = () => setShowPasswordDialog(false);

  // Keep the field name as "is_manager_in_call_center" (from your backend)
  const [formData, setFormData] = useState({
    username: userData.username || "",
    email: userData.email || "",
    phone: userData.phone || "",
    first_name: userData.first_name || "",
    last_name: userData.last_name || "",
    salary_amount: userData.salary_amount || "",
    contract_date: userData.contract_date || "",
    available_contract_date: userData.available_contract_date || "",
    department: userData.department || "",
    position: userData.position || "",
    is_manager_in_call_center: userData.is_manager_in_call_center || false,
    call_center_admin: userData.call_center_admin || false,
  });

  const [permissions, setPermissions] = useState({
    reception: userData.reception || false,
    pharmacy: userData.pharmacy || false,
    doctor: userData.doctor || false,
    nurse: userData.nurse || false,
    x_ray: userData.x_ray || false,
    ultrasounds: userData.ultrasounds || false,
    laboratory: userData.laboratory || false,
    echo: userData.echo || false,
    finance: userData.finance || false,
    reports: userData.reports || false,
    hr: userData.hr || false,
    academic: userData.academic || false,
    dental: userData.dental || false,
    call_center: userData.call_center || false,
    beauty: userData.beauty || false,
    htPatient: userData.htPatient || false,
    operations: userData.operations || false,
    emergency: userData.emergency || false,
    bedManagement: userData.bedManagement || false,
    ipd: userData.ipd || false,
    icu: userData.icu || false,
    night_shift: userData.night_shift || false,
    night_shift_manage: userData.night_shift_manage || false,
  });

  const [limitedDataView, setLimitedDataView] = useState(
    userData.limited_data_view || false
  );

  const [positionData, setPositionData] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);
  const [errors, setErrors] = useState({});
  const [existingDocuments, setExistingDocuments] = useState({
    image: userData.image || null,
    cv: userData.cv || null,
    educational_document: userData.educational_document || null,
  });

  // Fetch positions and departments
  const { data: positions, isSuccess: positionSuccess } = useFetchObjects(
    ["position"],
    "position",
    token
  );
  const { data: departments, isSuccess: departmentSuccess } = useFetchObjects(
    ["departments"],
    "departments",
    token
  );

  useEffect(() => {
    if (positionSuccess) setPositionData(positions);
  }, [positionSuccess, positions]);

  useEffect(() => {
    if (departmentSuccess) setDepartmentData(departments);
  }, [departmentSuccess, departments]);

  const { handleUpdate, loading } = useUpdate("users", token);

  // Validate required fields
  const validate = () => {
    let tempErrors = {};
    if (!formData.email) tempErrors.email = t("hr.emailRequired");
    if (!formData.phone) tempErrors.phone = t("hr.phoneNumberRequired");
    if (!formData.salary_amount)
      tempErrors.salary_amount = t("hr.salaryAmountRequired");
    if (!formData.contract_date)
      tempErrors.contract_date = t("hr.contractDateRequired");

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  // Handle text fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  // Handle file uploads
  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      // Limit file size to 5MB
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          [name]: t("hr.fileSizeError"),
        }));
        return;
      }
      setErrors((prev) => ({ ...prev, [name]: null }));
      setFormData((prevData) => ({ ...prevData, [name]: file }));
      setExistingDocuments((prev) => ({ ...prev, [name]: null })); // Clear existing file display
    }
  };

  // Handle permission checkboxes
  const handlePermissionChange = (e) => {
    const { name, checked } = e.target;
    setPermissions((prevPermissions) => ({
      ...prevPermissions,
      [name]: checked,
    }));
  };

  // Submit the main form
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const updatedData = new FormData();

    // Append all formData fields. For booleans (like is_manager_in_call_center) always append,
    // even if false.
    Object.keys(formData).forEach((key) => {
      if (
        formData[key] instanceof File ||
        typeof formData[key] === "boolean" ||
        formData[key]
      ) {
        updatedData.append(key, formData[key]);
      }
    });

    // Append permissions
    Object.keys(permissions).forEach((key) => {
      updatedData.append(key, permissions[key]);
    });

    // Append additional fields
    updatedData.append("limited_data_view", limitedDataView);

    // Update user, then navigate
    handleUpdate(userData.id, updatedData);
    navigate("/human-resource/employees");
  };

  return (
    <Container>
      <Box sx={{ paddingY: 4, maxWidth: "800px", marginX: "auto" }}>
        <Typography variant="h5" fontWeight={600} align="center" gutterBottom>
          {t("hr.editEmployee")}
        </Typography>

        {/* Personal Information */}
        <Box sx={{ marginTop: 3 }}>
          <Typography fontWeight={500} gutterBottom>
            {t("hr.personalInformation")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("hr.username")}
                name="username"
                value={formData.username}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                margin="dense"
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("hr.email")}
                name="email"
                value={formData.email}
                onChange={handleChange}
                type="email"
                variant="outlined"
                fullWidth
                margin="dense"
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("hr.firstName")}
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("hr.lastName")}
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                margin="dense"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("hr.phone")}
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                type="number"
                variant="outlined"
                fullWidth
                margin="dense"
                error={!!errors.phone}
                helperText={errors.phone}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Office Information */}
        <Box sx={{ marginTop: 4 }}>
          <Typography fontWeight={500} gutterBottom>
            {t("hr.officeInformation")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("hr.salaryAmount")}
                name="salary_amount"
                value={formData.salary_amount}
                onChange={handleChange}
                type="number"
                variant="outlined"
                fullWidth
                margin="dense"
                error={!!errors.salary_amount}
                helperText={errors.salary_amount}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("hr.contractDate")}
                name="contract_date"
                value={formData.contract_date}
                onChange={handleChange}
                type="date"
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                fullWidth
                margin="dense"
                error={!!errors.contract_date}
                helperText={errors.contract_date}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("hr.availableContractDate")}
                name="available_contract_date"
                value={formData.available_contract_date}
                onChange={handleChange}
                type="date"
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                fullWidth
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label={t("hr.position")}
                name="position"
                value={formData.position}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                margin="dense"
              >
                {positionData.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label={t("hr.department")}
                name="department"
                value={formData.department}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                margin="dense"
              >
                {departmentData.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Box>

        {/* Documents */}
        <Box sx={{ marginTop: 4 }}>
          <Typography fontWeight={500} gutterBottom>
            {t("hr.documents")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Button variant="outlined" component="label" fullWidth>
                {t("hr.uploadImage")}
                <input
                  type="file"
                  name="image"
                  hidden
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </Button>
              {existingDocuments.image && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t("hr.currentFile")}:{" "}
                  <a
                    href={existingDocuments.image}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none", color: "blue" }}
                  >
                    {existingDocuments.image}
                  </a>
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button variant="outlined" component="label" fullWidth>
                {t("hr.uploadCV")}
                <input
                  type="file"
                  name="cv"
                  hidden
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                />
              </Button>
              {existingDocuments.cv && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t("hr.currentFile")}:{" "}
                  <a
                    href={existingDocuments.cv}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none", color: "blue" }}
                  >
                    {existingDocuments.cv}
                  </a>
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" fullWidth>
                {t("hr.uploadEducationalDocument")}
                <input
                  type="file"
                  name="educational_document"
                  hidden
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                />
              </Button>
              {existingDocuments.educational_document && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t("hr.currentFile")}:{" "}
                  <a
                    href={existingDocuments.educational_document}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none", color: "blue" }}
                  >
                    {existingDocuments.educational_document}
                  </a>
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* Permissions */}
        <Box sx={{ marginTop: 4 }}>
          <Typography fontWeight={500} gutterBottom>
            {t("hr.permissions")}
          </Typography>
          <Grid container spacing={2}>
            {Object.keys(permissions)
              .filter(
                (p) => !["call_center", "beauty", "htPatient"].includes(p)
              )
              .map((permission) => (
              <Grid item xs={6} sm={4} md={3} key={permission}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={permissions[permission]}
                      onChange={handlePermissionChange}
                      name={permission}
                    />
                  }
                  label={t(`hr.${permission}`)}
                />
              </Grid>
            ))}

            {/* Volunteer option commented out
            <Grid item xs={6} sm={4} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={limitedDataView}
                    onChange={(e) => setLimitedDataView(e.target.checked)}
                    name="limitedDataView"
                  />
                }
                label={t("hr.limitedDataView")}
              />
            </Grid>
            */}

            {/* Call Center permission options commented out - not needed for now
            {permissions?.call_center && (
              <>
                {" "}
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="is_manager_in_call_center"
                        checked={formData.is_manager_in_call_center}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            is_manager_in_call_center: e.target.checked,
                          })
                        }
                      />
                    }
                    label={t("hr.managerCallCenter")}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="call_center_admin"
                        checked={formData.call_center_admin}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            call_center_admin: e.target.checked,
                          })
                        }
                      />
                    }
                    label={t("hr.callCenterAdmin", "Call Center Admin")}
                  />
                </Grid>
              </>
            )}
            */}
          </Grid>
        </Box>

        {/* Change Password Dialog */}
        {showPasswordDialog && (
          <ChangePasswordDialog
            open={showPasswordDialog}
            onClose={handleCloseDialog}
            userId={userData.id}
            token={token}
          />
        )}

        {/* Submit and Change Password Buttons */}
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {t("hr.updateEmployee")}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleOpenDialog}
            sx={{ ml: 2 }}
          >
            {t("hr.changePassword")}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default EmployeeEdit;
